<script setup lang="ts">
import { Button } from '@/components/ui/button'
import { useRouter } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'

const router = useRouter()
const auth = useAuth()

const handleLogout = async () => {
  await auth.logout()
  router.push('/auth/login')
}
</script>

<template>
  <div class="min-h-screen flex flex-col overflow-x-hidden">
    <!-- Desktop Header -->
    <header class="hidden sm:block mt-4">
      <div
        class="container mx-auto px-4 sm:px-10 max-w-xl h-16 flex items-center justify-between bg-white rounded-full shadow-md"
      >
        <div class="flex items-center gap-4 sm:gap-6">
          <router-link
            to="/"
            class="text-xl font-semibold hover:opacity-80 transition-opacity -mt-1"
          >
            Maximo
          </router-link>
          <nav class="flex items-center gap-4">
            <router-link
              to="/tax-refund-options"
              class="text-sm text-gray-600 hover:text-gray-900 transition-colors"
              active-class="text-primary font-medium"
            >
              Tax Refund
            </router-link>
            <router-link
              to="/linked-bank-accounts"
              class="text-sm text-gray-600 hover:text-gray-900 transition-colors"
              active-class="text-primary font-medium"
            >
              Bank Accounts
            </router-link>
          </nav>
        </div>

        <div class="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" class="gap-2">
                <i-heroicons-solid-user-circle class="h-5 w-5" />
                {{ auth.user.value?.first_name || 'Account' }}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" class="w-48">
              <DropdownMenuItem @click="router.push('/profile')">
                <i-heroicons-solid-user class="h-4 w-4 mr-2" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem @click="router.push('/pension-info')">
                <i-heroicons-wallet class="h-4 w-4 mr-2" />
                Pension Setup
              </DropdownMenuItem>
              <DropdownMenuItem @click="router.push('/settings')">
                <i-heroicons-solid-cog class="h-4 w-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem @click="handleLogout">
                <i-heroicons-solid-arrow-right-on-rectangle
                  class="h-4 w-4 mr-2"
                />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>

    <main class="flex-1 pb-16 sm:pb-0">
      <div class="container mx-auto px-4 max-w-xl">
        <slot></slot>
      </div>
    </main>

    <!-- Mobile Bottom Navigation -->
    <nav
      class="sm:hidden fixed bottom-0 left-0 right-0 bg-white z-50 rounded-full m-4 shadow-md"
    >
      <div class="flex justify-around items-center h-16">
        <router-link
          to="/landing"
          class="flex flex-col items-center p-2 text-gray-400"
          active-class="text-primary"
        >
          <i-heroicons-home-solid class="h-6 w-6 current" />
        </router-link>
        <router-link
          to="/linked-bank-accounts"
          class="flex flex-col items-center p-2 text-gray-400"
          active-class="text-primary"
        >
          <i-heroicons-credit-card-solid class="h-6 w-6 current" />
        </router-link>
        <router-link
          to="/tax-refund-options"
          class="flex flex-col items-center p-2 text-gray-400"
          active-class="text-primary"
        >
          <i-heroicons-receipt-refund-solid class="h-6 w-6 current" />
        </router-link>
        <router-link
          to="/profile"
          class="flex flex-col items-center p-2 text-gray-400"
          active-class="text-primary"
        >
          <i-heroicons-user-circle-solid class="h-6 w-6 current" />
        </router-link>
        <router-link
          to="/settings"
          class="flex flex-col items-center p-2 text-gray-400"
          active-class="text-primary"
        >
          <i-heroicons-cog-solid class="h-6 w-6 current" />
        </router-link>
      </div>
    </nav>
  </div>
</template>
